// RoleDetailPage.tsx — manage a single role: rename inline via the title chip,
// edit description, add/remove scopes, delete. Scope chips link out to
// ScopeDetailPage.
import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import NameTag from '../components/NameTag'
import Combobox from 'common/ui/Combobox'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import { BsPlusLg, BsSave, BsTrash } from 'react-icons/bs'
import Text from 'common/ui/Text'
import { useConfirm } from 'common/ui/ConfirmDialog'
import { useAuth, apiFetch } from '../hooks/useAuth'

interface RoleData {
    name: string
    description?: string
    permissions: string[]
}

const RoleDetailPage = () => {
    const { name = '' } = useParams<{ name: string }>()
    const navigate = useNavigate()
    const { email, loading, error: authError } = useAuth()
    const [role, setRole] = useState<RoleData | null>(null)
    const [allScopes, setAllScopes] = useState<string[]>([])
    const [editName, setEditName] = useState('')
    const [desc, setDesc] = useState('')
    const [newScope, setNewScope] = useState('')
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [fetchErr, setFetchErr] = useState('')

    const fetchAll = useCallback(async () => {
        setFetchErr('')
        try {
            const res = await apiFetch('/api/admin/roles')
            if (!res.ok) { setFetchErr('Failed to load roles'); return }
            const data = await res.json()
            const roles: RoleData[] = data.roles || []
            const found = roles.find((r) => r.name === name) || null
            setRole(found)
            if (found) {
                setEditName(found.name)
                setDesc(found.description || '')
            } else {
                setFetchErr(`Role "${name}" not found`)
            }
            const scopeSet = new Set<string>()
            for (const r of roles) for (const p of r.permissions || []) scopeSet.add(p)
            setAllScopes(Array.from(scopeSet).sort())
        } catch {
            setFetchErr('Failed to load role')
        }
    }, [name])

    useEffect(() => {
        if (!loading && email) fetchAll()
    }, [loading, email, fetchAll])

    const patchRole = async (body: Partial<RoleData>) => {
        const res = await apiFetch(`/api/admin/roles/${encodeURIComponent(name)}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        })
        if (!res.ok) {
            const data = await res.json().catch(() => ({}))
            throw new Error(data.error || 'Failed to update role')
        }
    }

    const handleAddScope = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(''); setSuccess('')
        const scope = newScope.trim()
        if (!scope || !role) return
        if ((role.permissions || []).includes(scope)) {
            setError(`Already grants ${scope}`)
            return
        }
        try {
            await patchRole({ permissions: [...(role.permissions || []), scope] })
            setSuccess(`Added ${scope}`)
            setNewScope('')
            fetchAll()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to add scope')
        }
    }

    const handleRemoveScope = async (scope: string) => {
        if (!role) return
        setError(''); setSuccess('')
        try {
            await patchRole({ permissions: (role.permissions || []).filter((p) => p !== scope) })
            setSuccess(`Removed ${scope}`)
            fetchAll()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to remove scope')
        }
    }

    const handleSaveDescription = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(''); setSuccess('')
        if (!role || desc === (role.description || '')) return
        try {
            await patchRole({ description: desc })
            setSuccess('Description updated')
            fetchAll()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to update description')
        }
    }

    const handleCommitName = useCallback(async () => {
        if (!role) return
        const target = editName.trim()
        if (!target) { setEditName(role.name); return }
        if (target === role.name) return
        setError(''); setSuccess('')
        try {
            const res = await apiFetch(`/api/admin/roles/${encodeURIComponent(role.name)}/rename`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ new_name: target }),
            })
            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                throw new Error(data.error || 'Rename failed')
            }
            navigate(`/roles/${encodeURIComponent(target)}`, { replace: true })
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Rename failed')
            setEditName(role.name)
        }
    }, [editName, role, navigate])

    const [confirm, confirmDialog] = useConfirm()

    const handleDelete = async () => {
        const ok = await confirm({
            title: 'Delete Role',
            body: <>Delete role <strong>{name}</strong>? This removes it from all assigned users.</>,
            confirmLabel: 'Delete',
        })
        if (!ok) return
        setError(''); setSuccess('')
        try {
            const res = await apiFetch(`/api/admin/roles/${encodeURIComponent(name)}`, { method: 'DELETE' })
            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                throw new Error(data.error || 'Delete failed')
            }
            navigate('/roles', { replace: true })
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Delete failed')
        }
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        )
    }

    if (fetchErr) {
        return (
            <>
                <h1 className="mb-4">Role</h1>
                <Alert variant="danger">{fetchErr}</Alert>
                <Link to="/roles">← Back to roles</Link>
            </>
        )
    }

    if (!role) return null

    return (
        <>
            <Stack direction="horizontal" className="mb-4 align-items-center">
                <h1 className="m-0 d-flex align-items-center gap-2">
                    <Link to="/roles" className="text-muted text-decoration-none">Roles</Link>
                    <span className="text-muted">/</span>
                    <NameTag
                        name={editName}
                        kind="role"
                        editable={{ onChange: setEditName, onCommit: handleCommitName }}
                    />
                </h1>
                <Button variant="outline-danger" size="sm" className="ms-auto d-flex align-items-center gap-1" onClick={handleDelete}>
                    <BsTrash /> Delete role
                </Button>
            </Stack>

            {authError && <Alert variant="danger">{authError}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="mb-3">
                <Card.Header>Description</Card.Header>
                <Card.Body>
                    <Form id="desc-form" onSubmit={handleSaveDescription}>
                        <Form.Group>
                            <Text
                                type="text"
                                style={{ width: "100%" }}
                                placeholder="optional"
                                value={desc}
                                onChange={(e) => setDesc(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Card.Body>
                <Card.Footer>
                    <Button variant="primary" type="submit" form="desc-form" disabled={desc === (role.description || '')} className="d-flex align-items-center gap-2">
                        <BsSave /> Save
                    </Button>
                </Card.Footer>
            </Card>

            <Card className="mb-3">
                <Card.Header>Scopes ({role.permissions?.length || 0})</Card.Header>
                <Card.Body>
                    {role.permissions && role.permissions.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {role.permissions.map((p) => (
                                <NameTag
                                    key={p}
                                    name={p}
                                    kind="scope"
                                    to={`/scopes/${encodeURIComponent(p)}`}
                                    actions={[{ icon: <BsTrash />, onClick: () => handleRemoveScope(p), label: `Remove ${p}`, tone: 'danger' }]}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted small">No scopes granted by this role.</p>
                    )}
                    <Form id="add-scope-form" onSubmit={handleAddScope}>
                        <Form.Group>
                            <Form.Label>Add scope</Form.Label>
                            <Combobox
                                value={newScope}
                                onChange={setNewScope}
                                options={allScopes}
                                style={{ width: "100%" }}
                                placeholder="scope.name (autocompletes from existing scopes)"
                            />
                        </Form.Group>
                    </Form>
                </Card.Body>
                <Card.Footer>
                    <Button variant="success" type="submit" form="add-scope-form" disabled={!newScope.trim()} className="d-flex align-items-center gap-2">
                        <BsPlusLg /> Add
                    </Button>
                </Card.Footer>
            </Card>

            {confirmDialog}
        </>
    )
}

export default RoleDetailPage
